import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';
import { CreateCheckoutPageBaseInputValidationSchema } from './create';
import { UpdateFunnelStepInputValidationSchema } from '../page/common/funnel';
import {
  UpdateRedirectUrlPathSegmentValidationSchema,
  UpdateRedirectUrlQueryParameterValidationSchema,
} from '../page/common/redirectAndConfirmation';
import { UpdatePageThemeIdInputValidationSchema } from '../page/common/themeId';
import { UpdatePageFormLayoutInputValidationSchema } from '../page/formLayout';

const checkoutPageUpdateBodyShape = {
  ...CreateCheckoutPageBaseInputValidationSchema.pick({
    name: true,
    slug: true,
    locale: true,
    customizeCheckoutConfirmation: true,
    status: true,
    testmode: true,
    allowDynamicTitle: true,
    allowDynamicDescription: true,
    closePopupOnClickOutside: true,
    hostedLayoutType: true,
    themeId: true,
    themeOverrides: true,
    redirect: true,
    afterPaymentAction: true,
    confirmationCheckoutTitle: true,
    confirmationCheckoutMessage: true,
    redirectUrl: true,
    redirectUrlInsideEmbed: true,
    redirectPageId: true,
    sendPaymentNotification: true,
    notifyEmail: true,
    sendEmailConfirmation: true,
    customizeEmailConfirmation: true,
    confirmationEmailSubject: true,
    confirmationEmailMessage: true,
    confirmationEmailShowLogo: true,
    confirmationEmailShowStoreName: true,
    googleIndex: true,
    trackingCodes: true,
    invoiceSettings: true,
    savePaymentMethod: true,
    paymentMethods: true,
    checkoutAbandonment: true,
    paymentOptions: true,
    fees: true,
    coverProcessingFees: true,
    showCouponCodeField: true,
    showCouponCodeFieldType: true,
    allowDynamicPrice: true,
    allowDynamicDiscountedFromPrice: true,
    allowDynamicRedirectUrl: true,
    imageIds: true,
    enableFileAccessForInactiveSubscriptions: true,
    limitSubscriptions: true,
    limitSubscriptionsStripe: true,
    sendCanceledSubscriptionNotifications: true,
    sendRecurringPaymentFailedNotifications: true,
    sendRecurringPaymentSucceededNotifications: true,
    allowDynamicPlanIterations: true,
    allowDynamicLabel: true,
    allowDynamicPreselectedPrice: true,
    tax: true,
    funnelSteps: true,
    redirectUrlPath: true,
    redirectUrlQuery: true,
  }).partial().extend({
    themeId: UpdatePageThemeIdInputValidationSchema,
    formLayout: UpdatePageFormLayoutInputValidationSchema.optional(),
    // Update can't create fields to key against (fields is excluded from
    // this body), so field references inside funnel steps and redirect
    // path/query segments must resolve by {fieldId}, not the create-time
    // {fieldKey} — mirrors apps/api/src/pages/services/updateCheckoutPage.ts.
    funnelSteps: z.array(UpdateFunnelStepInputValidationSchema).nullish().meta({
      description:
        'Ordered funnel configuration for this page. Supports intermediate checkout steps, one-click upsells, and confirmation behavior. Field references inside upsell/confirmation redirect steps resolve by `fieldId` (get current field IDs from the page detail response) — the create-time `fieldKey` string is rejected.',
    }),
    imageIds: z.array(ObjectIdStringValidationSchema).nullish().meta({
      description:
        'Array of images for the page gallery. All referenced file IDs must exist and belong to your account. Pass null to clear the gallery.',
    }),
    redirectUrlPath: z.array(UpdateRedirectUrlPathSegmentValidationSchema).nullish().meta({
      description:
        'Additional path parameters appended to the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` (the persisted field ID, from the page detail response) instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [
        { key: 'fields', fieldId: '507f1f77bcf86cd799439011' },
        { key: 'orderId', identifier: 'orderId' },
      ],
    }),
    redirectUrlQuery: z.array(UpdateRedirectUrlQueryParameterValidationSchema).nullish().meta({
      description:
        'Query parameters for the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [{ parameter: 'name', key: 'fields', fieldId: '507f1f77bcf86cd799439011' }],
    }),
  }).shape,
};

/**
 * The canonical list of fields a merchant may PATCH on a checkout page.
 * Derived from `checkoutPageUpdateBodyShape` so the API controller and the
 * MCP tool can iterate the same list without re-declaring it (drift-proof —
 * see `apps/api/src/pages/schemas/checkoutPageUpdateFields.driftGuard.test.ts`).
 */
export const CHECKOUT_PAGE_UPDATE_FIELDS = Object.keys(
  checkoutPageUpdateBodyShape,
) as ReadonlyArray<keyof typeof checkoutPageUpdateBodyShape>;

/**
 * Unrefined, non-strict base for the update body. The API's PATCH endpoint
 * builds on this (it stays lenient and adds API-only fields); the MCP body
 * below layers strict() and the at-least-one-field refine on top.
 */
export const CheckoutPageUpdateBodyBaseValidationSchema = z.object(checkoutPageUpdateBodyShape);

export const CheckoutPageUpdateBodyValidationSchema = z
  .object(checkoutPageUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  });

// MCP update tool input; themeOverrides is loose, see ThemeOverridesToolInputValidationSchema.
export const UpdateCheckoutPageInputValidationSchema = z
  .object({
    pageId: ObjectIdStringValidationSchema.meta({
      description: 'The ID of the checkout page to update.',
    }),
    ...checkoutPageUpdateBodyShape,
    themeOverrides: ThemeOverridesToolInputValidationSchema,
  })
  .refine(
    value => {
      const { pageId: _pageId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one update field must be provided.' },
  );

export type UpdateCheckoutPageInput = z.infer<typeof UpdateCheckoutPageInputValidationSchema>;
